import { ReactNode, createContext, useCallback, useContext, useEffect, useRef } from "react";

import Analytics from "./Analytics";
import { analytics } from "./appAnalytics";
import { AnalyticsEvent, AnalyticsScopeContext, TrackArguments } from "./events";

const AnalyticsContext = createContext<Analytics>(analytics);
const AnalyticsScopeReactContext = createContext<Partial<AnalyticsScopeContext>>({});

export const AnalyticsProvider = ({ children }: { children: ReactNode }) => (
    <AnalyticsContext.Provider value={analytics}>{children}</AnalyticsContext.Provider>
);

export const useAnalytics = () => useContext(AnalyticsContext);

type ScopeProps = {
    context: AnalyticsScopeContext;
    children: ReactNode;
};

/**
 * Adds its context to every event tracked below it, so a component deep in the page reports the
 * assignment it stands on without receiving it. Scopes do not nest: the closest one wins.
 */
export const AnalyticsScope = ({ context, children }: ScopeProps) => (
    <AnalyticsScopeReactContext.Provider value={context}>{children}</AnalyticsScopeReactContext.Provider>
);

/**
 * Tracks an event with the properties its type requires, enriched with the surrounding scope.
 *
 * The returned function keeps its identity when the scope changes, so an effect depending on it
 * does not track again because the assignment moved on.
 */
export const useTrackEvent = () => {
    const instance = useAnalytics();
    const scope = useContext(AnalyticsScopeReactContext);
    const scopeRef = useRef(scope);

    useEffect(() => {
        scopeRef.current = scope;
    });

    return useCallback(
        <E extends AnalyticsEvent>(event: E, ...[properties]: TrackArguments<E>) =>
            instance.dispatch(event, { ...scopeRef.current, ...properties }),
        [instance],
    );
};
